import React from "react";

// Material
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";

// Assets
import storingMobileBg from "../../assets/backgrounds/storing-evidence-mobile-bg.png";
import elipse from "../../assets/backgrounds/elipse.svg";
import lockIcon from "../../assets/icons/storingEvidence/lock.svg";
import incognitoIcon from "../../assets/icons/storingEvidence/incognito.svg";
import shareIcon from "../../assets/icons/storingEvidence/share.svg";
import lawyerIcon from "../../assets/icons/storingEvidence/lawyer.svg";
import orgIcon from "../../assets/icons/storingEvidence/org.svg";
import intelChip from "../../assets/icons/storingEvidence/kulpa-i-chip.svg";

// Components
import Section from "../Section/Section";
import Text, { types } from "../Text/Text";

// Config
import colors from "../../config/colors";

// Contents
import StoringEvidenceDesktop from "./StoringEvidenceDesktop";
import HomeSubsectionResponsive from "../HomeSubsection/HomeSubsectionResponsive";

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: 1500,
    paddingBottom: 100,
    paddingRight: "2.4vw",
    paddingLeft: "2.4vw",
    [theme.breakpoints.down("md")]: {
      minHeight: 1000,
      paddingBottom: 40
    },
    [theme.breakpoints.down("sm")]: {
      minHeight: 0,
      paddingBottom: 0,
      paddingRight: "0",
      paddingLeft: "0"
    },

    [`&:after`]: {
      [theme.breakpoints.up("md")]: {
        content: '""',
        position: "absolute",
        bottom: 0,
        left: 0,
        height: 383,
        width: 190,
        backgroundImage: `url(${elipse})`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center center",
        backgroundSize: "contain",
        transform: "translateY(25%)",
        zIndex: "99"
      }
    }
  },
  mainTitle: {
    maxWidth: 1344,
    textTransform: "capitalize",
    textAlign: "center",
    [theme.breakpoints.down("md")]: {
      maxWidth: 896
    },
    [theme.breakpoints.down("sm")]: {
      maxWidth: 530,
      width: "90%"
    }
  },
  brandSpan: {
    color: colors.lightBlue
  }
}));

const subsections = [
  {
    id: "doNothing",
    heading: "Don’t share",
    title: "Don’t share",
    icon: lockIcon,
    descriptions: [
      "You don’t need to take any immediate action. Your evidence will remain safe and secure on your kulpa cloud, ready for when you do want to share it."
    ],
    color: "#0B4283",
    mobileBg: storingMobileBg
  },
  {
    id: "organisation",
    heading: "Connected organisation",
    title: "Share with a connected organisation",
    icon: orgIcon,
    descriptions: [
      "We connect you with organisations you may want to share your evidence with.",
      "For example, if your incident is a crime and occurred within the jurisdiction of a connected police force that supports direct-reporting, you’ll see an in-app option to share with them."
    ],
    color: "#1A9FFF"
  },
  {
    id: "someone",
    heading: "Someone else",
    title: "Share with someone else",
    icon: shareIcon,
    description: "Police will receive immediately usable evidence.",
    descriptions: [
      "You’re always in control. Share your evidence with anyone you choose. Just select “Share with someone else,” enter their email and phone number, and securely send your encrypted file."
    ],
    color: "#3ACCE1"
  },
  {
    id: "lawyer",
    heading: "kulpa lawyer",
    title: "Share with a kulpa lawyer",
    icon: lawyerIcon,
    descriptions: [
      "Depending on the circumstances, our legal team may be able to support you with a civil legal action."
    ],
    color: "#E7F9FC"
  },
  {
    id: "anonymous",
    heading: "Anonymously",
    title: "Share anonymously",
    icon: incognitoIcon,
    descriptions: [
      "If you want to share evidence but want to remain anonymous - you can. Use the in-app option - ‘I am a witness who wants to remain anonymous.",
      "kulpa will protect your identity and the recipient will not be able to identify you."
    ],
    color: "#241879",
    badge: intelChip
  }
];

const StoringEvidenceSection = () => {
  const classes = useStyles();
  const isMobile = useMediaQuery(useTheme().breakpoints.down("sm"));

  return (
    <Section className={classes.root}>
      <Text
        type={types.miniSectionTitle}
        className={classes.mainTitle}
        component="h2"
      >
        Choose Who You Share It With
      </Text>
      {isMobile ? (
        <HomeSubsectionResponsive
          subsections={subsections}
          descriptionForTitle
        />
      ) : (
        <StoringEvidenceDesktop subsections={subsections} />
      )}
    </Section>
  );
};

export default StoringEvidenceSection;
